import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';
import type {
  SubscriptionPlan,
  SubscriptionResponse,
  CheckoutSessionResponse,
  PortalSessionResponse,
  CreateCheckoutPayload,
  CreatePortalPayload,
} from '@/lib/types';

const FIVE_MINUTES = 1000 * 60 * 5;

export function usePlans() {
  return useQuery({
    queryKey: queryKeys.billing.plans(),
    queryFn: async (): Promise<SubscriptionPlan[]> => {
      const response = await apiClient.get<SubscriptionPlan[]>('/api/v1/billing/plans/');
      return response.data;
    },
    staleTime: FIVE_MINUTES,
  });
}

export function useSubscription() {
  return useQuery({
    queryKey: queryKeys.billing.subscription(),
    queryFn: async (): Promise<SubscriptionResponse> => {
      const response = await apiClient.get<SubscriptionResponse>('/api/v1/billing/subscription/');
      return response.data;
    },
    staleTime: FIVE_MINUTES,
  });
}

export function useCreateCheckoutSession() {
  return useMutation({
    mutationFn: async (data: CreateCheckoutPayload): Promise<CheckoutSessionResponse> => {
      const response = await apiClient.post<CheckoutSessionResponse>(
        '/api/v1/billing/subscription/checkout/',
        data
      );
      return response.data;
    },
  });
}

export function useCreatePortalSession() {
  return useMutation({
    mutationFn: async (data: CreatePortalPayload): Promise<PortalSessionResponse> => {
      const response = await apiClient.post<PortalSessionResponse>(
        '/api/v1/billing/subscription/portal/',
        data
      );
      return response.data;
    },
  });
}

export function useStartTrial() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<SubscriptionResponse> => {
      const response = await apiClient.post<SubscriptionResponse>(
        '/api/v1/billing/subscription/start-trial/'
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.billing.subscription() });
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.modules() });
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.organization() });
    },
  });
}
